import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { SupabaseSelectQueryFilterOptions } from '@examdojo/core/supabase';
import {
  PaymentRequestStatusCellRendererComponent,
  PaymentRequestStatusFilterComponent,
  PayoutRequestService,
  PayoutRequestStatus,
  PayoutRequestUIModel,
} from '@examdojo/payments';
import { LoaderComponent } from '@examdojo/ui/loader';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem,
} from 'ag-grid-community';
import { EMPTY, finalize, first, tap } from 'rxjs';
import { PaymentRequestActionsCellRendererComponent } from './payment-request-actions-cell-renderer.component';
import { PaymentRequestExtraBonusCellRendererComponent } from './payment-request-extra-bonus-cell-renderer.component';
import { PaymentRequestHoursCellRendererComponent } from './payment-request-hours-cell-renderer.component';
import { PaymentRequestsListService } from './payment-request-list.service';

export interface PaymentRequestsConfig {
  userId?: string;
  showHeader?: boolean;
  showStatusFilter?: boolean;
  defaultStatusFilter?: PayoutRequestStatus | 'ALL';
  headerTitle?: string;
  compact?: boolean;
}

const DEFAULT_CONFIG: PaymentRequestsConfig = {
  showHeader: true,
  showStatusFilter: true,
  defaultStatusFilter: 'WAITING_FOR_APPROVAL',
  headerTitle: 'Payment Requests',
  compact: false,
};

@Component({
  selector: 'exam-payment-requests',
  template: `
    <div class="flex h-full flex-col bg-white dark:bg-gray-900">
      @if (finalConfig().showHeader) {
        <div class="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {{ finalConfig().headerTitle }}
          </h2>

          @if (finalConfig().showStatusFilter) {
            <dojo-payment-request-status-filter
              (statusFilterChanged)="updateAgGridStatusFilters($event)"
              [statusFilter]="agGridStatusFilter()"
            />
          }
        </div>
      }

      @if (finalConfig().showStatusFilter && !finalConfig().showHeader) {
        <div class="border-b border-gray-200 p-4 dark:border-gray-700">
          <dojo-payment-request-status-filter
            (statusFilterChanged)="updateAgGridStatusFilters($event)"
            [statusFilter]="agGridStatusFilter()"
          />
        </div>
      }

      <div class="relative h-full flex-1">
        <dojo-master-list-view
          [class]="'h-full'"
          [columnDefs]="columnDefs"
          [gridOptions]="gridOptions"
          [canAdd]="false"
          [showToolbarAddButton]="false"
          [hideSearch]="true"
          [agGridAdditionalClass]="'small-height'"
          (gridReady)="onGridReady($event)"
          [@appearOnTrigger]="!isLoading()"
        />

        @if (isLoading()) {
          <div class="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-900/50">
            <dojo-loader />
          </div>
        }
      </div>
    </div>
  `,
  styleUrl: './payment-requests.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [MasterListViewComponent, LoaderComponent, PaymentRequestStatusFilterComponent],
  providers: [provideSimpleListView(PaymentRequestsListService)],
  animations: [getAnimationAppearOnTrigger()],
})
export class PaymentRequestsComponent {
  constructor(
    private readonly payoutRequestService: PayoutRequestService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
  ) {
    // Initialize status filter based on config
    effect(() => {
      const defaultFilter = this.finalConfig().defaultStatusFilter!;
      this.agGridStatusFilter.set(defaultFilter);
    });

    // Apply initial filter when grid is ready
    effect(() => {
      if (this.gridApi()) {
        setTimeout(() => {
          this.updateAgGridStatusFilters(this.agGridStatusFilter());
        }, 100);
      }
    });

    // Hide/show user columns based on config
    effect(() => {
      const gridApi = this.gridApi();
      if (gridApi) {
        const shouldHideUserColumns = !!this.finalConfig().userId;
        gridApi.setColumnsVisible(['user_name', 'user_email'], !shouldHideUserColumns);
        // Resize columns to fit after hiding/showing columns
        setTimeout(() => {
          gridApi.sizeColumnsToFit();
        }, 0);
      }
    });
  }

  readonly config = input<PaymentRequestsConfig>({});
  readonly gridApi = signal<GridApi<PayoutRequestUIModel> | null>(null);
  readonly isLoading = signal<boolean>(false);

  readonly agGridStatusFilter = signal<PayoutRequestStatus | 'ALL'>('WAITING_FOR_APPROVAL');

  readonly finalConfig = computed(() => ({
    ...DEFAULT_CONFIG,
    ...this.config(),
  }));

  readonly columnDefs: Array<ColDef<PayoutRequestUIModel>> = [
    {
      field: 'user_name',
      headerName: 'Name',
      valueGetter: ({ data }) => data?.user_name ?? '—',
      pinned: 'left',
      cellClass: 'cursor-pointer hover:bg-sky-50',
      onCellClicked: (params) => {
        if (params.data?.user_id) {
          this.router.navigate(['/admin', params.data.user_id]);
        }
      },
    },
    {
      field: 'user_email',
      headerName: 'Email',
      valueGetter: ({ data }) => data?.user_email ?? '—',
    },
    {
      field: 'period',
      width: 130,
      headerName: 'Period',
      valueGetter: ({ data }) => data?.period ?? '—',
    },
    {
      field: 'status',
      width: 250,
      headerName: 'Status',
      cellRenderer: PaymentRequestStatusCellRendererComponent,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [
          'DRAFT',
          'WAITING_FOR_APPROVAL',
          'APPROVED',
          'REJECTED',
          'PAYMENT_WAITING_FOR_APPROVAL',
          'PAYMENT_IN_PROGRESS',
          'PAYMENT_SUCCEEDED',
          'PAYMENT_FAILED',
        ],
      },
      suppressHeaderFilterButton: true,
    },
    {
      field: 'totalHours',
      headerName: 'Hours Submitted',
      cellRenderer: PaymentRequestHoursCellRendererComponent,
      sortable: false,
      width: 150,
    },
    {
      field: 'estimatedPayout',
      headerName: 'Hourly Payout',
      width: 160,
      valueGetter: ({ data }) => (data?.estimatedPayout ? `€${data.estimatedPayout.toFixed(2)}` : '€0.00'),
      sortable: false,
    },
    {
      field: 'extra_bonus_in_eur',
      headerName: 'Extra Bonus',
      cellRenderer: PaymentRequestExtraBonusCellRendererComponent,
      editable: false,
      sortable: false,
    },
    {
      field: 'calculatedFinalPayout',
      headerName: 'Final Payout',
      valueGetter: ({ data }) => {
        const finalPayout = data?.calculatedFinalPayout;
        return finalPayout !== null && finalPayout !== undefined ? `€${finalPayout.toFixed(2)}` : '—';
      },
    },
    {
      headerName: 'Action',
      cellRenderer: PaymentRequestActionsCellRendererComponent,
      width: 280,
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      pinned: 'right',
    },
  ];

  readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const { startRow, endRow, sortModel, filterModel } = params.request;
      const sortBy: SortModelItem[] = sortModel.length ? sortModel : [{ colId: 'created_at', sort: 'desc' }];

      this.isLoading.set(true);

      // Convert ag-grid FilterModel to SupabaseSelectQueryConfig
      const supabaseFilters: Record<string, SupabaseSelectQueryFilterOptions> = {};

      // Apply user_id filter if provided in config
      const userId = this.finalConfig().userId;
      if (userId) {
        supabaseFilters['user_id'] = {
          filter: userId,
          filterType: 'text',
          type: 'equals',
        };
      }

      // Convert ag-grid status filter to Supabase format
      if (filterModel && 'status' in filterModel) {
        const statusFilter = filterModel['status'] as { filterType?: string; values?: string[] } | undefined;
        if (statusFilter?.filterType === 'set' && statusFilter.values) {
          supabaseFilters['status'] = {
            filterType: 'set',
            values: statusFilter.values,
          };
        }
      }

      this.payoutRequestService
        .fetchPaginatedListView({
          startRow,
          endRow: endRow ? endRow - 1 : undefined,
          sortBy,
          filterBy: supabaseFilters,
        })
        .pipe(
          first(),
          tap({
            next: (data) => {
              params.success({ rowData: data.data, rowCount: data.totalCount });
            },
            error: (error: unknown) => {
              console.error('Failed to load payment requests:', error);
              params.fail();
            },
          }),
          this.errorHandlerService.catchError(
            `[PaymentRequestsComponent.getRows]: failed to load the payment requests`,
            () => EMPTY,
            { toast: `Could not load payment requests` },
          ),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    },
  };

  readonly gridOptions: GridOptions<PayoutRequestUIModel> = {
    pagination: true,
    paginationAutoPageSize: true,
    cacheBlockSize: 100,
    rowModelType: 'serverSide',
    serverSideDatasource: this.serverSideDatasource,
    suppressCellFocus: true,
    suppressContextMenu: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No payment requests found',
      body: 'No payment requests are currently available with the selected filters',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      floatingFilter: false,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    getRowId: (params) => (params.data?.id ? `${params.data.id}` : ''),
    maxBlocksInCache: 10,
  };

  onGridReady(event: GridReadyEvent<PayoutRequestUIModel>) {
    this.gridApi.set(event.api);
    // Auto-size columns to fit available space
    event.api.sizeColumnsToFit();
  }

  updateAgGridStatusFilters(status: PayoutRequestStatus | 'ALL') {
    const gridApi = this.gridApi();
    if (!gridApi) {
      return;
    }

    const filterModel = gridApi.getFilterModel();

    if (status === 'ALL') {
      delete filterModel['status'];
    } else {
      filterModel['status'] = {
        filterType: 'set',
        values: [status],
      };
    }

    gridApi.setFilterModel(filterModel);
  }
}
